import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import axios from 'axios';
import { v4 as uuidv4 } from "uuid";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { OrderProcessingStatus, OrderBusinessStatusEnum } from "../../utils/enums.js";

export default function ShipmentNoteDetails({
  details,
  pickupList,
  loadingDetails,
  onUseDefault,
  onApplyPaymentAll,
  onApplyShipmentOrderTypeAll,
  onBatchUpdateRows,
  onUpdateRow,
  onVerify,
  onUnverify,
  onBatchVerify,
  onSubmit,
}) {
  const [carrierOptionsBySeq, setCarrierOptionsBySeq] = useState({});
  const [loadingCarrierSeqs, setLoadingCarrierSeqs] = useState(new Set());

  //Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const paginatedNotes = details.shipment_notes.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // ─── 1) Modal “editing” state ───────────────────────────────────────────
  const [editingSeq, setEditingSeq] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [loadingLabelRows, setLoadingLabelRows] = useState({});
  const [loadingDownloadSelected, setLoadingDownloadSelected] = useState(false);

  // ─── 2) Loading indicators ─────────────────────────────────────────────
  const [loadingRows, setLoadingRows] = useState({}); // per‐row verify/unverify
  const [loadingApplyPickup, setLoadingApplyPickup] = useState(false);
  const [loadingApplyPayment, setLoadingApplyPayment] = useState(false);
  const [loadingApplyShipmentOrderType, setLoadingApplyShipmentOrderType] = useState(false);
  const [loadingVerifySelected, setLoadingVerifySelected] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingSubmitAll, setLoadingSubmitAll] = useState(false);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  // ─── 3) “Select Rows” state ─────────────────────────────────────────────
  const [selectedSeqs, setSelectedSeqs] = useState(() => new Set());

  // ─── 4) “Apply to All” dropdown selections ─────────────────────────────
  const [selectedPickupForAll, setSelectedPickupForAll] = useState(
    pickupList.length > 0 ? pickupList[0] : null
  );
  const [selectedPaymentForAll, setSelectedPaymentForAll] = useState("PREPAID");
  const [selectedShipmentOrderTypeForAll, setSelectedShipmentOrderTypeForAll] = useState("FORWARD");

  const isOrderCreatedLabel = (status) => OrderProcessingStatus.toLabel(status) === "Order Created";

  const statusBySeq = useMemo(() => {
    const m = new Map();
    (details.shipment_notes || []).forEach(sn => {
      m.set(sn.shipment_note_sequence_number, sn.order_processing_status);
    });
    return m;
  }, [details]);

  const seqToOrderId = (seq) =>
    details.shipment_notes.find(s => s.shipment_note_sequence_number === seq)
      ?.shipment_notes_order_data?.order_id;

  const handleApplyPickupDefault = async (pickupAddressObj, seqsArray = []) => {
    const orderIds = seqsArray.map(seqToOrderId).filter(Boolean);
    await onUseDefault(pickupAddressObj, orderIds);
  };

  const handleManualCourierSelection = async () => {
    const correlationIdMap = new Map();
    const selected = Array.from(selectedSeqs);
    setLoadingCarrierSeqs(new Set(selected));
    if (selected.length === 0) return;

    const authToken = localStorage.getItem("authToken");
    const allocatedCarriersRaw = localStorage.getItem("allocatedCarriers");

    let allocatedCarriers;
    try {
      allocatedCarriers = JSON.parse(allocatedCarriersRaw) || [];
    } catch (e) {
      console.error("Invalid allocatedCarriers in localStorage", e);
      alert("Carrier information is not available or invalid.");
      return;
    }

    if (!authToken || allocatedCarriers.length === 0) {
      alert("Missing authentication token or allocated carriers.");
      return;
    }

    const confirmAndSubmit = async () => {
      if (loadingSubmitAll) return;
      setLoadingSubmitAll(true);
      try {
        await onSubmit();
        setShowConfirmSubmit(false);
        setAcknowledged(false);
      } catch (e) {
        console.error("Submit (confirm) failed", e);
        setShowConfirmSubmit(false);
        setAcknowledged(false);
        alert("Submission failed");
      } finally {
        setLoadingSubmitAll(false);
      }
    };

    const carrierIdsToEvaluate = allocatedCarriers.map((c) => c.carrier_partner_id);

    const requests = selected.map((seq) => {
      const sn = details.shipment_notes.find(
        (s) => s.shipment_note_sequence_number === seq
      );
      const order = sn.shipment_notes_order_data;

      const correlationId = uuidv4();
      correlationIdMap.set(correlationId, seq);

      return {
        correlation_id: correlationId,
        carrier_options_calculation_context: {
          shipment_type: order.shipment_order_type,
          payment_mode: order.order_type,
          invoice_value: order.grand_total,
          rvp_with_quality_check: false,
          weight_in_grams: order.package_details.dead_weight_in_grams,
          length_in_cm: order.package_details.length_in_cm,
          breadth_in_cm: order.package_details.breadth_in_cm,
          height_in_cm: order.package_details.height_in_cm,
          pickup_pincode: order.pickup_address_data?.pickup_postal_code,
          drop_pincode: order.drop_address_data.postal_code,
        },
        carrier_partner_ids_to_evaluate: carrierIdsToEvaluate,
      };
    });
    const baseUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_RETRIEVE_CARRIER_OPTIONS_IN_BATCH}`;
    const payload = Array.isArray(requests) ? requests : [requests];
    try {
      const res = await axios.post(
        baseUrl,
        payload,
        {
          headers: {
            Authorization: authToken,
            "Content-Type": "application/json",
          },
        }
      );

      const updatedMap = {};

      for (let result of res.data) {
        const { correlation_id, carrier_options_calculation_result_by_carrier_partner_id } = result;
        const seq = correlationIdMap.get(correlation_id);
        if (!seq) continue;

        const serviceableOptions = Object.entries(carrier_options_calculation_result_by_carrier_partner_id)
          .filter(([_, val]) => val.serviceable)
          .map(([id, val]) => ({
            id,
            name: val.carrier_partner_public_name,
            price: val.total_amount_to_charge_after_tax,
          }));

        updatedMap[seq] = serviceableOptions;

        if (serviceableOptions.length > 0) {
          const cheapest = serviceableOptions.reduce((min, o) => (o.price < min.price ? o : min), serviceableOptions[0]);
          const sn = details.shipment_notes.find(s => s.shipment_note_sequence_number === seq);
          if (sn) {
            const nextOrderData = {
              ...sn.shipment_notes_order_data,
              carrier_partner_id: cheapest.id,
            };
            // Persist via parent -> server
            await onUpdateRow(seq, nextOrderData);
          }
        }
      }

      setCarrierOptionsBySeq((prev) => ({ ...prev, ...updatedMap }));
      setLoadingCarrierSeqs(new Set());
    } catch (err) {
      console.error("Manual courier selection failed:", err);
      setLoadingCarrierSeqs(new Set());
      alert("Failed to fetch courier options. See console for details.");
    }
  };

  const handleUseRecommendationRule = async () => {
    for (const seq of Array.from(selectedSeqs)) {
      const sn = details.shipment_notes.find(s => s.shipment_note_sequence_number === seq);
      if (!sn) continue;
      const nextOrderData = { ...sn.shipment_notes_order_data };
      delete nextOrderData.carrier_partner_id;
      await onUpdateRow(seq, nextOrderData);
    }
    setCarrierOptionsBySeq(prev => {
      const clone = { ...prev };
      for (const seq of Array.from(selectedSeqs)) delete clone[seq];
      return clone;
    });
  };

  useEffect(() => {
    if (pickupList.length > 0) {
      setSelectedPickupForAll(pickupList[0]);
    }
  }, [pickupList]);

  // ─── 5) Open row‐edit modal ─────────────────────────────────────────────
  const openEditModal = (sn) => {
    const incomingItems = sn.shipment_notes_order_data.items || [];

    const dropAddr = sn.shipment_notes_order_data.drop_address_data || {};
    const dropWithCountry = {
      ...dropAddr,
      country_code: dropAddr.country_code || "IN",
    };

    setEditingSeq(sn.shipment_note_sequence_number);
    setModalData({
      ...sn.shipment_notes_order_data,
      drop_address_data: dropWithCountry,
      items: [...incomingItems],
      order_processing_status: sn.order_processing_status,
      wayuvega_create_order_processing_status: sn.wayuvega_create_order_processing_status,
      shipment_note_sequence_number: sn.shipment_note_sequence_number,
    });
  };



  // ─── 6) Close row‐edit modal ───────────────────────────────────────────
  const closeModal = () => {
    setEditingSeq(null);
    setModalData(null);
  };

  // ─── 7) Save modal changes (batch‐update) ──────────────────────────────
  const saveModalChanges = async () => {
    if (!modalData) return;
    const seq = modalData.shipment_note_sequence_number;
    setLoadingSave(true);

    const toUpdate = {
      ...modalData,
      drop_address_data: { ...modalData.drop_address_data },
      pickup_address_data: modalData.pickup_address_data
        ? { ...modalData.pickup_address_data }
        : null,
      package_details: { ...modalData.package_details },
      items: Array.isArray(modalData.items)
        ? modalData.items.map((it) => ({ ...it }))
        : [],
      grand_total: Number(modalData.grand_total),
      order_type: modalData.order_type,
      shipment_order_type: modalData.shipment_order_type,
      order_processing_status: modalData.order_processing_status,
      wayuvega_create_order_processing_status: modalData.wayuvega_create_order_processing_status,
      shipment_note_sequence_number: modalData.shipment_note_sequence_number,
    };

    try {
      await onUpdateRow(seq, toUpdate);
      closeModal();
    } catch (e) {
      console.error("Row update failed", e);
    } finally {
      setLoadingSave(false);
    }
  };

  // ─── 8) Is a row “complete”? (used to enable Verify button) ─────────────
  const isRowComplete = (orderData) => {
    // 1) Drop address must have all fields
    const d = orderData.drop_address_data;
    if (!d.address_line || !d.city || !d.state || !d.postal_code || !d.contact) {
      return false;
    }
    // 2) Pickup address (all required)
    const p = orderData.pickup_address_data;
    if (
      !p ||
      !p.pickup_address ||
      !p.pickup_city ||
      !p.pickup_state ||
      !p.pickup_postal_code ||
      !p.pickup_poc ||
      !p.pickup_organisation ||
      !p.pickup_poc_contact_number ||
      !d.country_code
    ) {
      return false;
    }
    // 3) Package details (>0)
    const pkg = orderData.package_details;
    if (
      !pkg ||
      pkg.length_in_cm <= 0 ||
      pkg.breadth_in_cm <= 0 ||
      pkg.height_in_cm <= 0 ||
      pkg.dead_weight_in_grams <= 0
    ) {
      return false;
    }
    // 4) Items: must exist and each item must be valid
    if (!orderData.items || orderData.items.length === 0) return false;
    for (let it of orderData.items) {
      if (
        it.quantity <= 0 ||
        !it.description ||
        !it.sku ||
        it.unit_price <= 0 ||
        it.total <= 0
      ) {
        return false;
      }
    }
    // 5) grand_total must be > 0
    if (!orderData.grand_total || orderData.grand_total <= 0) return false;
    // 6) Payment + Shipment‐Type must be set
    if (!orderData.order_type || !orderData.shipment_order_type) {
      return false;
    }
    return true;
  };

  const seqEligibilityMap = useMemo(() => {
    const m = new Map();
    (details.shipment_notes || []).forEach((sn) => {
      const od = sn.shipment_notes_order_data;
      const status = sn.order_processing_status;

      const isCreated = isOrderCreatedLabel(status);
      const isVerified = status === OrderProcessingStatus.VERIFIED_BY_MERCHANT;
      const complete = isRowComplete(od);

      m.set(sn.shipment_note_sequence_number, !isCreated && !isVerified && complete);
    });
    return m;
  }, [details]);

  const selectedHasOrderCreated = useMemo(() => {
    if (!selectedSeqs || selectedSeqs.size === 0) return false;
    for (let seq of selectedSeqs) {
      const st = statusBySeq.get(seq);
      if (isOrderCreatedLabel(st)) return true;
    }
    return false;
  }, [selectedSeqs, statusBySeq, isOrderCreatedLabel]);

  const selectedHasIneligibleForAction = useMemo(() => {
    if (!selectedSeqs || selectedSeqs.size === 0) return false;
    for (let seq of selectedSeqs) {
      const st = statusBySeq.get(seq);
      if (isOrderCreatedLabel(st) || !seqEligibilityMap.get(seq)) return true;
    }
    return false;
  }, [selectedSeqs, seqEligibilityMap, statusBySeq]);

  const selectedAllAreOrderCreated = useMemo(() => {
    if (!selectedSeqs || selectedSeqs.size === 0) return false;
    for (let seq of selectedSeqs) {
      const st = statusBySeq.get(seq);
      if (!isOrderCreatedLabel(st)) return false;
    }
    return true;
  }, [selectedSeqs, statusBySeq]);

  const allOnPageSelected = useMemo(() => {
    return paginatedNotes.every(sn => selectedSeqs.has(sn.shipment_note_sequence_number));
  }, [paginatedNotes, selectedSeqs]);


  const toggleSelectAll = () => {
    setSelectedSeqs(prev => {
      const next = new Set(prev);
      const deselect = allOnPageSelected;
      paginatedNotes.forEach(sn => {
        const seq = sn.shipment_note_sequence_number;
        deselect ? next.delete(seq) : next.add(seq);
      });
      return next;
    });
  };


  // ─── 9) Per‐row Verify / Unverify ───────────────────────────────────────
  const handleVerifyClick = async (order_id, seq) => {
    setLoadingRows((prev) => ({ ...prev, [seq]: true }));
    try {
      await onVerify(order_id);
    } catch (e) {
      console.error("Verify API failed", e);
    } finally {
      setLoadingRows((prev) => ({ ...prev, [seq]: false }));
    }
  };

  const handleUnverifyClick = async (order_id, seq) => {
    setLoadingRows((prev) => ({ ...prev, [seq]: true }));
    try {
      await onUnverify(order_id);
    } catch (e) {
      console.error("Unverify API failed", e);
    } finally {
      setLoadingRows((prev) => ({ ...prev, [seq]: false }));
    }
  };

  // ─── 10) Select / Deselect one row ─────────────────────────────────────
  const toggleSelectSeq = (seq) => {
    setSelectedSeqs((prev) => {
      const clone = new Set(prev);
      if (clone.has(seq)) clone.delete(seq);
      else clone.add(seq);
      return clone;
    });
  };

  // ─── 12) Batch Verify “Selected” rows ──────────────────────────────────
  const handleVerifySelected = async () => {
    const seqsArray = Array.from(selectedSeqs);
    if (seqsArray.length === 0) return;

    const orderIds = seqsArray.map((seq) => {
      const sn = details.shipment_notes.find(
        (s) => s.shipment_note_sequence_number === seq
      );
      return sn.shipment_notes_order_data.order_id;
    });

    setLoadingVerifySelected(true);
    try {
      await onBatchVerify(orderIds);
      setSelectedSeqs(new Set());
    } catch (e) {
      console.error("Batch verify selected failed", e);
    } finally {
      setLoadingVerifySelected(false);
    }
  };

  const confirmAndSubmit = async () => {
    if (loadingSubmitAll) return;
    setLoadingSubmitAll(true);
    try {
      await onSubmit();
      setShowConfirmSubmit(false);
      setAcknowledged(false);
    } catch (e) {
      console.error("Submit (confirm) failed", e);
      setShowConfirmSubmit(false);
      setAcknowledged(false);
      alert("Submission failed. Check console for details.");
    } finally {
      setLoadingSubmitAll(false);
    }
  };

  // Apply Pickup to Selected
  const handleApplyPickupSelected = async () => {
    if (selectedSeqs.size === 0 || !selectedPickupForAll) return;
    setLoadingApplyPickup(true);
    try {
      const payload = buildBatchPayload((current) => ({
        ...current,
        pickup_address_data: { ...selectedPickupForAll },
      }));
      await runBatchUpdate(payload);
    } catch (e) {
      console.error("Apply to Selected (Pickup) failed", e);
    } finally {
      setLoadingApplyPickup(false);
    }
  };

  // Apply Payment to Selected
  const handleApplyPaymentSelected = async () => {
    if (selectedSeqs.size === 0 || !selectedPaymentForAll) return;
    setLoadingApplyPayment(true);
    try {
      const payload = buildBatchPayload((current) => ({
        ...current,
        order_type: selectedPaymentForAll,
      }));
      await runBatchUpdate(payload);
    } catch (e) {
      console.error("Apply to Selected (Payment) failed", e);
    } finally {
      setLoadingApplyPayment(false);
    }
  };

  // Apply Shipment Order Type to Selected
  const handleApplyShipmentOrderTypeSelected = async () => {
    if (selectedSeqs.size === 0 || !selectedShipmentOrderTypeForAll) return;
    setLoadingApplyShipmentOrderType(true);
    try {
      const payload = buildBatchPayload((current) => ({
        ...current,
        shipment_order_type: selectedShipmentOrderTypeForAll,
      }));
      await runBatchUpdate(payload);
    } catch (e) {
      console.error("Apply to Selected (Shipment Order Type) failed", e);
    } finally {
      setLoadingApplyShipmentOrderType(false);
    }
  };


  // ─── 16) Submit Verified Orders (bottom button) ───────────────────────
  const handleSubmitAll = async () => {
    setLoadingSubmitAll(true);
    try {
      await onSubmit();
    } catch (e) {
      console.error("Submit Verified Orders failed", e);
    } finally {
      setLoadingSubmitAll(false);
    }
  };

  const buildBatchPayload = (patchMaker) => {
    return Array.from(selectedSeqs)
      .map((seq) => {
        const sn = details.shipment_notes.find(
          (s) => s.shipment_note_sequence_number === seq
        );
        if (!sn) return null;

        const current = sn.shipment_notes_order_data;

        const next = patchMaker({ ...current });

        return {
          shipment_note_sequence_number: sn.shipment_note_sequence_number,
          shipment_notes_order_data: {
            ...current,
            ...next,
            order_processing_status: sn.order_processing_status,
            shipment_note_sequence_number: sn.shipment_note_sequence_number,
          },
        };
      })
      .filter(Boolean);
  };

  const runBatchUpdate = async (payload) => {
    if (onBatchUpdateRows) {
      await onBatchUpdateRows(payload);
    } else {
      await Promise.all(
        payload.map(({ shipment_note_sequence_number, shipment_notes_order_data }) =>
          onUpdateRow(shipment_note_sequence_number, shipment_notes_order_data)
        )
      );
    }
    setSelectedSeqs(new Set());
  };

  // ─── Utility: download merged labels from API ─────────────────────────────────
  const downloadShipmentLabels = async (fileId, orderRefs) => {
    try {
      const authToken = localStorage.getItem('authToken');
      const merchantId = localStorage.getItem('merchantId');
      const apiUrl = `${process.env.REACT_APP_POCAS_BASE_URL}` +
        `${process.env.REACT_APP_BULK_MERGED_LABEL_DOWNLOAD_BY_COMMA_SEPARATED_ORDER_REFERENCE_NUMBER}` +
        merchantId;

      const payload = orderRefs.map(ref => ({ order_reference_number: ref }));

      const { data } = await axios.post(apiUrl, payload, {
        headers: { Authorization: authToken }
      });

      window.open(data.label_csv_file_url, '_blank', 'noopener,noreferrer');

    } catch (err) {
      console.error('Could not open labels URL:', err);
      alert('Failed to open labels. Check console for details.');
    }
  };

  // ─── Handlers for label download ─────────────────────────────────────────
  // Single row
  const handleDownloadLabelSingle = async (orderRef, seq) => {
    setLoadingLabelRows(prev => ({ ...prev, [seq]: true }));
    try {
      await downloadShipmentLabels(details.file_id, [orderRef]);
    } catch (e) {
      console.error("Label download failed", e);
    } finally {
      setLoadingLabelRows(prev => ({ ...prev, [seq]: false }));
    }
  };

  // Batch for selected rows
  const handleDownloadLabelSelected = async () => {
    const orderRefs = Array.from(selectedSeqs)
      .map(seq => {
        const sn = details.shipment_notes.find(
          s => s.shipment_note_sequence_number === seq
        );
        return sn?.shipment_notes_order_data.order_id;
      })
      .filter(Boolean);
    if (orderRefs.length === 0) return;

    setLoadingDownloadSelected(true);
    try {
      await downloadShipmentLabels(details.file_id, orderRefs);
      setSelectedSeqs(new Set());
    } catch (e) {
      console.error("Batch label download failed", e);
    } finally {
      setLoadingDownloadSelected(false);
    }
  };

  const isDownloadableStatus = (status) => {
    const label = OrderProcessingStatus.toLabel(status);
    return label === "Order Created" || label === "Pending Processing";
  };

  const selectedAllAreDownloadable = useMemo(() => {
    if (!selectedSeqs || selectedSeqs.size === 0) return false;
    for (let seq of selectedSeqs) {
      const st = statusBySeq.get(seq);
      if (!isDownloadableStatus(st)) return false;
    }
    return true;
  }, [selectedSeqs, statusBySeq]);


  // ────────────────────────────────────────────────────────────────────────
  // Prepare memoized defaults (so we don’t re‐compute each render)
  const defaultPayment = useMemo(() => selectedPaymentForAll, [selectedPaymentForAll]);
  const defaultShipmentType = useMemo(
    () => selectedShipmentOrderTypeForAll,
    [selectedShipmentOrderTypeForAll]
  );

  return (
    <div className="relative max-w-[90vw] mx-auto mt-6 mb-12 p-6 bg-white rounded-lg shadow-xl overflow-y-auto">
      {loadingSubmitAll && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60"
          aria-live="assertive"
          aria-busy="true"
        >
          <div className="flex flex-col items-center gap-4">
            <DotLottieReact
              src="https://lottie.host/7c65c753-b78b-4df6-adbf-f881d0bef1aa/Y85AFb3fbY.lottie"
              loop
              autoplay
              style={{ width: 160, height: 160 }}
            />
            <div className="text-white font-medium text-lg">Submitting orders…</div>
          </div>
        </div>
      )}
      {/* 1) Header */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-800">
          Details for{" "}
          <span className="font-mono text-indigo-700">
            {details.uploaded_file_name}{" "}
            <span className="text-base text-gray-500">({details.file_id})</span>
          </span>
        </h2>
      </div>

      {/* 2) “Apply to All” row (now acts on chosen rows) */}
      <div className="flex flex-wrap items-end gap-4 mb-4">
        {/* 2a) Pickup Location */}
        {pickupList.length > 0 && (
          <div className="flex items-center space-x-2">
            <select
              value={JSON.stringify(selectedPickupForAll || {})}
              onChange={(e) =>
                setSelectedPickupForAll(JSON.parse(e.target.value))
              }
              className="min-w-[100px] max-w-[600px] border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-indigo-300 transition"
              disabled={loadingDetails}
            >
              {pickupList.map((p, idx) => (
                <option key={idx} value={JSON.stringify(p)}>
                  {`${p.pickup_address}, ${p.pickup_city}, ${p.pickup_state} (${p.pickup_organisation})`}
                </option>
              ))}
            </select>
            <button
              onClick={handleApplyPickupSelected}
              disabled={
                selectedSeqs.size === 0 ||
                loadingApplyPickup ||
                loadingDetails ||
                selectedHasOrderCreated
              }
              className={`px-4 py-2 bg-purple-600 text-white font-medium rounded-md shadow-md hover:bg-purple-700 transition ${selectedSeqs.size === 0 || loadingApplyPickup || loadingDetails
                ? "opacity-50 cursor-not-allowed"
                : ""
                }`}
            >
              {loadingApplyPickup ? (
                <svg className="animate-spin h-5 w-5 text-white mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
              ) : (
                "Apply Pickup to Chosen"
              )}
            </button>
          </div>
        )}

        {/* 2b) Payment Mode */}
        <div className="flex items-center space-x-2">
          <select
            value={selectedPaymentForAll}
            onChange={(e) => setSelectedPaymentForAll(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-indigo-300 transition"
            disabled={loadingDetails}
          >
            <option value="">— Select Payment —</option>
            <option value="PREPAID">PREPAID</option>
            <option value="COD">COD</option>
          </select>
          <button
            onClick={handleApplyPaymentSelected}
            disabled={
              selectedSeqs.size === 0 ||
              loadingApplyPayment ||
              loadingDetails ||
              selectedHasOrderCreated
            }
            className={`px-4 py-2 bg-green-600 text-white font-medium rounded-md shadow-md hover:bg-green-700 transition ${selectedSeqs.size === 0 || loadingApplyPayment || loadingDetails
              ? "opacity-50 cursor-not-allowed"
              : ""
              }`}
          >
            {loadingApplyPayment ? (
              <svg className="animate-spin h-5 w-5 text-white mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            ) : (
              "Apply Payment to Chosen"
            )}
          </button>
        </div>

        {/* 2c) Shipment Order Type */}
        <div className="flex items-center space-x-2">
          <select
            value={selectedShipmentOrderTypeForAll}
            onChange={(e) => setSelectedShipmentOrderTypeForAll(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-indigo-300 transition"
            disabled={loadingDetails}
          >
            <option value="">— Select Shipment Type —</option>
            <option value="FORWARD">FORWARD</option>
            <option value="REVERSE">REVERSE</option>
          </select>
          <button
            onClick={handleApplyShipmentOrderTypeSelected}
            disabled={
              selectedSeqs.size === 0 ||
              loadingApplyShipmentOrderType ||
              loadingDetails ||
              selectedHasOrderCreated
            }
            className={`px-4 py-2 bg-yellow-500 text-white font-medium rounded-md shadow-md hover:bg-yellow-600 transition ${selectedSeqs.size === 0 || loadingApplyShipmentOrderType || loadingDetails
              ? "opacity-50 cursor-not-allowed"
              : ""
              }`}
          >
            {loadingApplyShipmentOrderType ? (
              <svg className="animate-spin h-5 w-5 text-white mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            ) : (
              "Apply Shipment Type to Chosen"
            )}
          </button>
        </div>

        {/* 2d) Verify Chosen (disabled if any selected is ineligible) */}
        <button
          onClick={handleVerifySelected}
          disabled={
            selectedSeqs.size === 0 || loadingVerifySelected || loadingDetails || selectedHasOrderCreated
          }
          className={`flex items-center px-4 py-2 bg-teal-600 text-white font-medium rounded-md shadow-md hover:bg-teal-700 transition ${(selectedSeqs.size === 0 || loadingVerifySelected || loadingDetails || selectedHasOrderCreated)
            ? "opacity-50 cursor-not-allowed"
            : ""
            }`}
        >
          {loadingVerifySelected ? (
            <svg
              className="animate-spin h-5 w-5 text-white mx-auto"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              />
            </svg>
          ) : (
            "Verify Chosen"
          )}
        </button>

        {/* Download Labels for Chosen */}
        <button
          onClick={handleDownloadLabelSelected}
          disabled={
            selectedSeqs.size === 0 ||
            loadingDownloadSelected ||
            !selectedAllAreDownloadable
          }
          className={`ml-4 px-4 py-2 bg-indigo-500 text-white rounded-md shadow hover:bg-indigo-600 transition ${selectedSeqs.size === 0 || loadingDownloadSelected || !selectedAllAreDownloadable
            ? "opacity-50 cursor-not-allowed"
            : ""
            }`}
        >
          {loadingDownloadSelected ? (
            <svg
              className="animate-spin h-5 w-5 text-white mx-auto"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          ) : (
            "Download Labels for Chosen"
          )}
        </button>

        {/* Manual Courier Selection (disabled if any selected ineligible) */}
        <button
          className="px-4 py-2 bg-slate-600 text-white rounded-md shadow hover:bg-slate-700 transition"
          disabled={selectedSeqs.size === 0 || selectedHasIneligibleForAction}
          onClick={handleManualCourierSelection}
          style={{ opacity: selectedSeqs.size === 0 || selectedHasIneligibleForAction ? 0.5 : 1 }}
        >
          Manual Courier for Chosen
        </button>

        {/* Use Recommendation Rule (disabled if any selected ineligible) */}
        <button
          className="px-4 py-2 bg-blue-500 text-white rounded-md shadow hover:bg-blue-600 transition"
          disabled={selectedSeqs.size === 0 || selectedHasIneligibleForAction}
          onClick={handleUseRecommendationRule}
          style={{ opacity: selectedSeqs.size === 0 || selectedHasIneligibleForAction ? 0.5 : 1 }}
        >
          Use Recommendation for Chosen
        </button>

      </div>


      {/* 3) Table of Shipment Notes */}
      <div className="overflow-x-auto my-6">
        <table className="w-full border-collapse">
          <thead className="bg-gray-100 sticky top-0 z-10">
            <tr className="text-gray-700 text-sm">
              <th className="p-3 text-center">
                <input
                  type="checkbox"
                  onChange={toggleSelectAll}
                  checked={
                    details.shipment_notes.length > 0 &&
                    paginatedNotes
                      .map(sn => sn.shipment_note_sequence_number)
                      .every(seq => selectedSeqs.has(seq))
                  }
                />
              </th>
              <th className="p-3">SEQ</th>
              <th className="p-3 w-[9vw]">ORDER ID</th>
              <th className="p-3">ITEM</th>
              <th className="p-3">DROP ADDRESS</th>
              <th className="p-3">PICKUP LOCATION</th>
              <th className="p-3">COURIER SELECT</th>
              <th className="p-3">ORDER STATUS</th>
              <th className="p-3">CREATE ORDER STATUS</th>
              <th className="p-3 w-[10vw]">ACTIONS</th>
            </tr>
          </thead>

          <tbody>
            {paginatedNotes.map((sn, idx) => {
              const od = sn.shipment_notes_order_data;
              const status = sn.order_processing_status;
              const orderStatusLabel = OrderProcessingStatus.toLabel(status);
              const isOrderCreatedNow = orderStatusLabel === "Order Created";

              // compute per-row downloadable flag
              const isDownloadableNow = isDownloadableStatus(status);

              // Is “Order Created / Failed Creation”?
              const isOrderCreated =
                status ===
                OrderProcessingStatus.SHIPPMENT_NOTES_GENERATION_FOR_ORDER_PROCESSING_COMPLETED ||
                status ===
                OrderProcessingStatus.SHIPPMENT_NOTES_GENERATION_FOR_ORDER_PROCESSING_FAILED;
              const isVerified = status === OrderProcessingStatus.VERIFIED_BY_MERCHANT;
              const complete = isRowComplete(od);

              const seq = sn.shipment_note_sequence_number;
              const loadingThisRow = loadingRows[seq] === true;

              const canSelect = true;
              const isChecked = selectedSeqs.has(seq);

              // **Guard against empty items** 
              const primaryItem =
                Array.isArray(od.items) && od.items.length > 0
                  ? od.items[0]
                  : null;
              const dims = od.package_details;

              // Payment & Shipment‐Type badges
              const paymentMode = od.order_type;
              const shipmentOrderType = od.shipment_order_type;

              // “Create Order Status” lookup
              const wayuStatusCode = sn.wayuvega_create_order_processing_status;
              const wayuStatusLabel =
                Object.values(OrderBusinessStatusEnum).find(
                  (e) => e.code === wayuStatusCode
                )?.name || "No Status Exist";

              return (
                <tr
                  key={seq}
                  className={
                    idx % 2 === 0
                      ? "bg-white hover:bg-indigo-50"
                      : "bg-gray-50 hover:bg-indigo-50"
                  }
                >
                  {/* Checkbox */}
                  <td className="p-3 text-center">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleSelectSeq(seq)}
                    />
                  </td>

                  {/* SEQ */}
                  <td className="p-3 text-gray-600">{seq}</td>

                  {/* ORDER ID */}
                  <td className="p-3 font-mono text-xs text-gray-800 break-all">
                    {od.order_id}
                  </td>

                  {/* ITEM */}
                  <td className="p-3">
                    {primaryItem ? (
                      <>
                        <div className="font-medium text-gray-800">
                          {primaryItem.description}
                        </div>
                        <div className="text-gray-600 text-xs mt-1 space-y-0.5">
                          <div>
                            <strong>Qty:</strong> {primaryItem.quantity} &nbsp;|&nbsp;{" "}
                            <strong>SKU:</strong> {primaryItem.sku} &nbsp;|&nbsp;{" "}
                            <strong>Unit ₹:</strong> {primaryItem.unit_price.toFixed(2)}
                          </div>
                          <div>
                            <em className="mr-2">
                              Dims: {dims.length_in_cm}×{dims.breadth_in_cm}×
                              {dims.height_in_cm} cm
                            </em>
                            <em>Wt: {dims.dead_weight_in_grams} g</em>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-red-500 italic text-xs">No items</div>
                    )}

                    {/* Payment / Shipment‐Type badges */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {paymentMode ? (
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${paymentMode === "PREPAID"
                            ? "bg-green-100 text-green-800"
                            : "bg-orange-100 text-orange-800"
                            }`}
                        >
                          {paymentMode}
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                          PAYMENT MISSING
                        </span>
                      )}
                      {shipmentOrderType ? (
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${shipmentOrderType === "FORWARD"
                            ? "bg-indigo-100 text-indigo-800"
                            : "bg-purple-100 text-purple-800"
                            }`}
                        >
                          {shipmentOrderType}
                        </span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
                          SHIPMENT TYPE MISSING
                        </span>
                      )}
                    </div>
                  </td>

                  {/* DROP ADDRESS */}
                  <td className="p-3">
                    <div className="text-gray-800">
                      {`${od.drop_address_data.address_line}, ${od.drop_address_data.city}, ${od.drop_address_data.state} ${od.drop_address_data.postal_code}`}
                    </div>
                    <div className="text-gray-600 text-xs">
                      {od.drop_address_data.contact}{" "}
                      {od.drop_address_data.email && `| ${od.drop_address_data.email}`}
                    </div>
                  </td>

                  {/* PICKUP LOCATION */}
                  <td className="p-3">
                    {od.pickup_address_data ? (
                      <div className="text-gray-800">
                        {`${od.pickup_address_data.pickup_address}, ${od.pickup_address_data.pickup_city}, ${od.pickup_address_data.pickup_state} ${od.pickup_address_data.pickup_postal_code}`}
                      </div>
                    ) : (
                      <span className="text-red-500 italic text-xs">Not set</span>
                    )}
                  </td>

                  {/* Courier selection Manual or by recommendation rule */}
                  <td className="p-3 align-middle text-sm text-gray-900 whitespace-nowrap">
                    <div className="flex items-center justify-center min-h-[42px] w-full">
                      {loadingCarrierSeqs.has(seq) ? (
                        <div className="flex items-center gap-2">
                          <DotLottieReact
                            src="https://lottie.host/7c65c753-b78b-4df6-adbf-f881d0bef1aa/Y85AFb3fbY.lottie"
                            loop
                            autoplay
                            style={{ height: 28, width: 28 }}
                          />
                          <span className="text-indigo-600 font-medium">Fetching couriers...</span>
                        </div>
                      ) : carrierOptionsBySeq[seq] ? (
                        <div className="relative w-[220px]">
                          <select
                            className="w-full appearance-none bg-white border border-gray-300 text-sm px-3 py-2 pr-8 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all cursor-pointer font-medium text-gray-700"
                            value={od.carrier_partner_id || ""}
                            onChange={(e) => {
                              const selectedId = e.target.value;
                              const snIndex = details.shipment_notes.findIndex(
                                (s) => s.shipment_note_sequence_number === seq
                              );
                              const updated = [...details.shipment_notes];
                              if (selectedId === "RECOMMENDATION") {
                                delete updated[snIndex].shipment_notes_order_data.carrier_partner_id;
                              } else {
                                updated[snIndex].shipment_notes_order_data.carrier_partner_id = selectedId;
                              }
                              onUpdateRow(seq, updated[snIndex].shipment_notes_order_data);
                            }}
                          >
                            <option value="RECOMMENDATION" className="text-gray-500">
                              🔍 Use Recommendation Rule
                            </option>
                            {carrierOptionsBySeq[seq].map((opt) => (
                              <option key={opt.id} value={opt.id}>
                                {`${opt.name} — ₹${opt.price.toFixed(2)}`}
                              </option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-400">
                            <svg
                              className="h-4 w-4"
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0l-4.24-4.24a.75.75 0 01.02-1.06z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">No courier options</span>
                      )}
                    </div>
                  </td>


                  {/* ORDER PROCESSING STATUS badge */}
                  <td className="p-3">
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${status === OrderProcessingStatus.VERIFIED_BY_MERCHANT
                        ? "bg-green-100 text-green-800"
                        : status ===
                          OrderProcessingStatus.PENDING_VERIFICATION_BY_MERCHANT
                          ? "bg-yellow-100 text-yellow-800"
                          : status ===
                            OrderProcessingStatus.VERIFIED_AND_PENDING_ORDER_PROCESSING_AT_WAYUVEGA
                            ? "bg-indigo-100 text-indigo-800"
                            : status ===
                              OrderProcessingStatus.VERIFIED_AND_SUCCESSFUL_ORDER_PROCESSING_AT_WAYUVEGA
                              ? "bg-blue-100 text-blue-800"
                              : status ===
                                OrderProcessingStatus.VERIFIED_AND_FAILED_ORDER_PROCESSING_AT_WAYUVEGA
                                ? "bg-red-100 text-red-800"
                                : "bg-gray-100 text-gray-800"
                        }`}
                    >
                      {OrderProcessingStatus.toLabel(status)}
                    </span>
                  </td>

                  {/* CREATE ORDER STATUS badge */}
                  <td className="p-3">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                      {wayuStatusLabel}
                    </span>
                  </td>

                  {/* ACTIONS: Edit & Verify/Unverify */}
                  <td className="p-3">
                    <div className="grid grid-cols-2 grid-rows-2 gap-2">
                      {/* EDIT button */}
                      <button
                        onClick={() => openEditModal(sn)}
                        disabled={isOrderCreatedNow || loadingThisRow || loadingDetails}
                        className={`h-8 px-3 text-xs rounded-md font-medium flex-shrink-0 justify-center flex items-center transition ${isOrderCreatedNow || loadingThisRow || loadingDetails
                          ? "bg-blue-300 text-white cursor-not-allowed"
                          : "bg-blue-600 text-white hover:bg-blue-700"
                          }`}
                      >
                        {loadingThisRow && editingSeq === seq ? (
                          <svg
                            className="animate-spin h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8v8H4z"
                            />
                          </svg>
                        ) : (
                          "Edit"
                        )}
                      </button>

                      {/* VERIFY / UNVERIFY button */}
                      {isVerified ? (
                        <button
                          onClick={() => handleUnverifyClick(od.order_id, seq)}
                          disabled={isOrderCreatedNow || loadingThisRow || loadingDetails}
                          className={`h-8 px-3 text-xs rounded-md text-white flex-shrink-0 justify-center flex items-center transition ${isOrderCreatedNow || loadingThisRow || loadingDetails
                            ? "bg-red-300 cursor-not-allowed"
                            : "bg-red-500 hover:bg-red-600"
                            }`}
                        >
                          {loadingThisRow ? (
                            <svg
                              className="animate-spin h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v8H4z"
                              />
                            </svg>
                          ) : (
                            "Unverify"
                          )}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleVerifyClick(od.order_id, seq)}
                          disabled={
                            isOrderCreatedNow || !complete || loadingThisRow || loadingDetails
                          }
                          className={`h-8 px-3 text-xs rounded-md text-white flex-shrink-0 justify-center flex items-center transition ${isOrderCreatedNow || !complete || loadingThisRow || loadingDetails
                            ? "bg-orange-300 cursor-not-allowed"
                            : "bg-green-600 hover:bg-green-700"
                            }`}
                        >
                          {loadingThisRow ? (
                            <svg
                              className="animate-spin h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8v8H4z"
                              />
                            </svg>
                          ) : (
                            "Verify"
                          )}
                        </button>
                      )}
                      {/* ─── Label button ─────────────────────────────────────── */}
                      <button
                        onClick={() => handleDownloadLabelSingle(od.order_id, seq)}
                        disabled={loadingLabelRows[seq] || loadingDetails || !isDownloadableNow}
                        className={`h-8 px-3 text-xs rounded-md text-white transition ${loadingLabelRows[seq] || loadingDetails || !isDownloadableNow
                          ? "bg-purple-300 cursor-not-allowed"
                          : "bg-purple-600 hover:bg-purple-700"
                          }`}
                      >
                        {loadingLabelRows[seq] ? (
                          <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                          </svg>
                        ) : (
                          "Label"
                        )}
                      </button>

                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="flex justify-between items-center mt-4 text-sm text-gray-700">
          {/* Rows per page */}
          <div className="flex items-center space-x-2">
            <label htmlFor="rowsPerPage">Rows per page:</label>
            <select
              id="rowsPerPage"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border rounded px-2 py-1"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          {/* Pagination Buttons */}
          <div className="space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
            >
              ⬅️ Prev
            </button>
            <button
              onClick={() => setCurrentPage((prev) => prev + 1)}
              disabled={currentPage * rowsPerPage >= details.shipment_notes.length}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
            >
              Next ➡️
            </button>
          </div>
        </div>

      </div>

      {/* 4) Bottom “Submit Verified Orders” */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={() => setShowConfirmSubmit(true)}
          disabled={loadingSubmitAll}
          className={`px-6 py-2 bg-indigo-600 text-white font-medium rounded-lg shadow-md hover:bg-indigo-700 transition ${loadingSubmitAll ? "opacity-50 cursor-not-allowed" : ""
            }`}
        >
          {loadingSubmitAll ? (
            <svg
              className="animate-spin h-5 w-5 text-white mx-auto"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              />
            </svg>
          ) : (
            "Submit Verified Orders"
          )}
        </button>
      </div>
      {/* ─── Confirm “I acknowledge I have verified everything” before final submit ──────────────────── */}
      {showConfirmSubmit && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
          onClick={() => {
            setShowConfirmSubmit(false);
            setAcknowledged(false);
          }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Title + close “×” */}
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold text-gray-800">
                🛑 Before You Submit…
              </h3>
              <button
                onClick={() => {
                  setShowConfirmSubmit(false);
                  setAcknowledged(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {/* Checkbox + message */}
            <div className="mb-4 text-gray-700">
              <label className="inline-flex items-center">
                <input
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(e) => setAcknowledged(e.target.checked)}
                  className="form-checkbox h-5 w-5 text-indigo-600"
                />
                <span className="ml-2 text-sm">
                  I confirm I have reviewed and verified all selected orders.
                </span>
              </label>
            </div>

            {/* Buttons */}
            <div className="text-right space-x-2">
              <button
                onClick={() => {
                  setShowConfirmSubmit(false);
                  setAcknowledged(false);
                }}
                className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmAndSubmit}
                disabled={!acknowledged || loadingSubmitAll}
                className={`px-4 py-2 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700 transition ${(!acknowledged || loadingSubmitAll)
                  ? "opacity-50 cursor-not-allowed"
                  : ""
                  }`}
              >
                {loadingSubmitAll ? (
                  <svg
                    className="animate-spin h-5 w-5 text-white mx-auto"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                ) : (
                  "Yes, Submit Now"
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* 5) Modal for editing a single row */}
      {editingSeq !== null && modalData && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
          <div className="bg-white w-[90%] max-w-4xl h-[90%] overflow-y-auto rounded-lg shadow-lg p-6 relative">
            {/* Inner “×” to close just the row‐edit modal */}
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800"
            >
              ✕
            </button>

            <h3 className="text-lg font-semibold mb-6">
              Edit Order #{modalData.order_id}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* ORDER ID (read‐only) & Order Date (read‐only) */}
              <div className="col-span-1">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Order ID
                </label>
                <input
                  type="text"
                  value={modalData.order_id}
                  readOnly
                  className="w-full bg-gray-100 border rounded-md px-3 py-2 text-gray-700"
                />
              </div>
              <div className="col-span-1">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Order Date
                </label>
                <input
                  type="text"
                  value={new Date(modalData.order_date_utc).toLocaleString()}
                  readOnly
                  className="w-full bg-gray-100 border rounded-md px-3 py-2 text-gray-700"
                />
              </div>

              {/* Payment Type & Shipping Service */}
              <div className="col-span-1">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Payment Type
                </label>
                <select
                  value={modalData.order_type || ""}
                  onChange={(e) =>
                    setModalData((prev) => ({
                      ...prev,
                      order_type: e.target.value,
                    }))
                  }
                  className="w-full border rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-300"
                >
                  <option value="">— Select Payment —</option>
                  <option value="PREPAID">PREPAID</option>
                  <option value="COD">COD</option>
                </select>
              </div>
              <div className="col-span-1">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Shipping Service
                </label>
                <input
                  type="text"
                  value={modalData.shipping_service}
                  onChange={(e) =>
                    setModalData((prev) => ({
                      ...prev,
                      shipping_service: e.target.value,
                    }))
                  }
                  className="w-full border rounded-md px-3 py-2 text-gray-700"
                />
              </div>

              {/* Seller Name & Buyer Name */}
              <div className="col-span-1">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Seller Name
                </label>
                <input
                  type="text"
                  value={modalData.seller_name}
                  onChange={(e) =>
                    setModalData((prev) => ({
                      ...prev,
                      seller_name: e.target.value,
                    }))
                  }
                  className="w-full border rounded-md px-3 py-2 text-gray-700"
                />
              </div>
              <div className="col-span-1">
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Buyer Name
                </label>
                <input
                  type="text"
                  value={modalData.buyer_name}
                  onChange={(e) =>
                    setModalData((prev) => ({
                      ...prev,
                      buyer_name: e.target.value,
                    }))
                  }
                  className="w-full border rounded-md px-3 py-2 text-gray-700"
                />
              </div>

              {/* Drop Address */}
              <div className="col-span-2">
                <h4 className="text-lg font-semibold mb-2">Drop Address</h4>
                <div className="space-y-2">
                  <input
                    type="text"
                    placeholder="Address Line"
                    value={modalData.drop_address_data.address_line}
                    onChange={(e) =>
                      setModalData((prev) => ({
                        ...prev,
                        drop_address_data: {
                          ...prev.drop_address_data,
                          address_line: e.target.value,
                        },
                      }))
                    }
                    className="w-full border rounded-md px-3 py-2 text-gray-700"
                  />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="City"
                      value={modalData.drop_address_data.city}
                      onChange={(e) =>
                        setModalData((prev) => ({
                          ...prev,
                          drop_address_data: {
                            ...prev.drop_address_data,
                            city: e.target.value,
                          },
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2 text-gray-700"
                    />
                    <input
                      type="text"
                      placeholder="Country"
                      value={modalData.drop_address_data.country_code || "IN"}
                      onChange={(e) =>
                        setModalData((prev) => ({
                          ...prev,
                          drop_address_data: {
                            ...prev.drop_address_data,
                            country_code: e.target.value,
                          },
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2 text-gray-700"
                    />
                    <input
                      type="text"
                      placeholder="State"
                      value={modalData.drop_address_data.state}
                      onChange={(e) =>
                        setModalData((prev) => ({
                          ...prev,
                          drop_address_data: {
                            ...prev.drop_address_data,
                            state: e.target.value,
                          },
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2 text-gray-700"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                      type="text"
                      placeholder="PIN"
                      value={modalData.drop_address_data.postal_code}
                      onChange={(e) =>
                        setModalData((prev) => ({
                          ...prev,
                          drop_address_data: {
                            ...prev.drop_address_data,
                            postal_code: e.target.value,
                          },
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2 text-gray-700"
                    />
                    <input
                      type="text"
                      placeholder="Contact"
                      value={modalData.drop_address_data.contact}
                      onChange={(e) =>
                        setModalData((prev) => ({
                          ...prev,
                          drop_address_data: {
                            ...prev.drop_address_data,
                            contact: e.target.value,
                          },
                        }))
                      }
                      className="w/full border rounded-md px-3 py-2 text-gray-700"
                    />
                    <input
                      type="email"
                      placeholder="Email (optional)"
                      value={modalData.drop_address_data.email || ""}
                      onChange={(e) =>
                        setModalData((prev) => ({
                          ...prev,
                          drop_address_data: {
                            ...prev.drop_address_data,
                            email: e.target.value,
                          },
                        }))
                      }
                      className="w/full border rounded-md px-3 py-2 text-gray-700"
                    />
                  </div>
                </div>
              </div>

              {/* Pickup Location */}
              <div className="col-span-2">
                <h4 className="text-lg font-semibold mb-2">Pickup Location</h4>
                <select
                  value={
                    modalData.pickup_address_data
                      ? JSON.stringify(modalData.pickup_address_data)
                      : ""
                  }
                  onChange={(e) => {
                    const parsed = e.target.value ? JSON.parse(e.target.value) : null;
                    setModalData((prev) => ({
                      ...prev,
                      pickup_address_data: parsed,
                    }));
                  }}
                  className="w-full border rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-300"
                >
                  <option value="">— Select Pickup Location —</option>
                  {pickupList.map((p, idx) => (
                    <option key={idx} value={JSON.stringify(p)}>
                      {`${p.pickup_address}, ${p.pickup_city}, ${p.pickup_state} (${p.pickup_organisation})`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Payment & Shipment Order Type */}
              <div className="col-span-2">
                <h4 className="text-lg font-semibold mb-2">
                  Payment & Shipment Order Type
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Payment Type */}
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      Payment Type
                    </label>
                    <select
                      value={modalData.order_type || ""}
                      onChange={(e) =>
                        setModalData((prev) => ({
                          ...prev,
                          order_type: e.target.value,
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-300"
                    >
                      <option value="">— Select Payment —</option>
                      <option value="PREPAID">PREPAID</option>
                      <option value="COD">COD</option>
                    </select>
                  </div>

                  {/* Shipment Order Type */}
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      Shipment Order Type
                    </label>
                    <select
                      value={modalData.shipment_order_type || ""}
                      onChange={(e) =>
                        setModalData((prev) => ({
                          ...prev,
                          shipment_order_type: e.target.value,
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2 outline-none focus:ring-2 focus:ring-indigo-300"
                    >
                      <option value="">— Select Shipment Type —</option>
                      <option value="FORWARD">FORWARD</option>
                      <option value="REVERSE">REVERSE</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Package Details */}
              <div className="col-span-2">
                <h4 className="text-lg font-semibold mb-2">Package Details</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <input
                    type="number"
                    min="1"
                    placeholder="Length (cm)"
                    value={modalData.package_details.length_in_cm}
                    onChange={(e) =>
                      setModalData((prev) => ({
                        ...prev,
                        package_details: {
                          ...prev.package_details,
                          length_in_cm: Number(e.target.value),
                        },
                      }))
                    }
                    className="w-full border rounded-md px-3 py-2 text-gray-700"
                  />
                  <input
                    type="number"
                    min="1"
                    placeholder="Breadth (cm)"
                    value={modalData.package_details.breadth_in_cm}
                    onChange={(e) =>
                      setModalData((prev) => ({
                        ...prev,
                        package_details: {
                          ...prev.package_details,
                          breadth_in_cm: Number(e.target.value),
                        },
                      }))
                    }
                    className="w/full border rounded-md px-3 py-2 text-gray-700"
                  />
                  <input
                    type="number"
                    min="1"
                    placeholder="Height (cm)"
                    value={modalData.package_details.height_in_cm}
                    onChange={(e) =>
                      setModalData((prev) => ({
                        ...prev,
                        package_details: {
                          ...prev.package_details,
                          height_in_cm: Number(e.target.value),
                        },
                      }))
                    }
                    className="w/full border rounded-md px-3 py-2 text-gray-700"
                  />
                  <input
                    type="number"
                    min="1"
                    placeholder="Dead Weight (g)"
                    value={modalData.package_details.dead_weight_in_grams}
                    onChange={(e) =>
                      setModalData((prev) => ({
                        ...prev,
                        package_details: {
                          ...prev.package_details,
                          dead_weight_in_grams: Number(e.target.value),
                        },
                      }))
                    }
                    className="w/full border rounded-md px-3 py-2 text-gray-700"
                  />
                </div>
              </div>

              {/* Item Details Section */}
              {/* ─── Item Details Section ─────────────────────────────── */}
              <div className="col-span-2">
                <h4 className="text-lg font-semibold mb-2">Item(s)</h4>

                {/* If `items` is missing or empty, show placeholder + one “Add Item” button */}
                {(!Array.isArray(modalData.items) || modalData.items.length === 0) && (
                  <div className="mb-4">
                    <div className="text-gray-500 italic text-sm mb-2">
                      No items defined yet
                    </div>
                    <button
                      onClick={() => {
                        // Append a brand-new blank item
                        const newItem = {
                          quantity: 1,
                          description: "",
                          sku: "",
                          unit_price: 0,
                          tax: 0,
                          subtotal: 0,
                          total: 0,
                        };
                        setModalData((prev) => ({
                          ...prev,
                          items: [newItem],
                        }));
                      }}
                      className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition"
                    >
                      + Add Item
                    </button>
                  </div>
                )}

                {/* If there are items, map over them. Each item can be removed. */}
                {Array.isArray(modalData.items) &&
                  modalData.items.map((it, idx) => (
                    <div
                      key={idx}
                      className="border p-4 rounded-md mb-4 bg-gray-50 relative"
                    >
                      {/* → A small “×” to remove this particular item */}
                      <button
                        onClick={() => {
                          const newItems = [...modalData.items];
                          newItems.splice(idx, 1);
                          setModalData((prev) => ({
                            ...prev,
                            items: newItems,
                          }));
                        }}
                        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
                        title="Remove this item"
                      >
                        ×
                      </button>

                      <label className="block text-gray-700 text-sm font-medium mb-1">
                        Description
                      </label>
                      <input
                        type="text"
                        placeholder="Item description"
                        value={it.description}
                        onChange={(e) => {
                          const newItems = [...modalData.items];
                          newItems[idx].description = e.target.value;
                          setModalData((prev) => ({
                            ...prev,
                            items: newItems,
                          }));
                        }}
                        className="w/full border rounded-md px-3 py-2 text-gray-700 mb-3"
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-1">
                            Quantity
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={it.quantity}
                            onChange={(e) => {
                              const newItems = [...modalData.items];
                              newItems[idx].quantity = Number(e.target.value);
                              // Recompute subtotal & total
                              newItems[idx].subtotal =
                                (newItems[idx].unit_price * newItems[idx].quantity) /
                                (1 + newItems[idx].tax / 100);
                              newItems[idx].total =
                                newItems[idx].unit_price * newItems[idx].quantity;
                              setModalData((prev) => ({
                                ...prev,
                                items: newItems,
                              }));
                            }}
                            className="w/full border rounded-md px-3 py-2 text-gray-700"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-1">
                            SKU
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. AZ-716"
                            value={it.sku}
                            onChange={(e) => {
                              const newItems = [...modalData.items];
                              newItems[idx].sku = e.target.value;
                              setModalData((prev) => ({
                                ...prev,
                                items: newItems,
                              }));
                            }}
                            className="w/full border rounded-md px-3 py-2 text-gray-700"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-1">
                            Unit Price (₹)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={it.unit_price}
                            onChange={(e) => {
                              const newItems = [...modalData.items];
                              newItems[idx].unit_price = Number(e.target.value);
                              // Recompute:
                              newItems[idx].subtotal =
                                (newItems[idx].unit_price * newItems[idx].quantity) /
                                (1 + newItems[idx].tax / 100);
                              newItems[idx].total =
                                newItems[idx].unit_price * newItems[idx].quantity;
                              setModalData((prev) => ({
                                ...prev,
                                items: newItems,
                              }));
                            }}
                            className="w/full border rounded-md px-3 py-2 text-gray-700"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-1">
                            Tax (%)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={it.tax}
                            onChange={(e) => {
                              const newItems = [...modalData.items];
                              newItems[idx].tax = Number(e.target.value);
                              // Recompute:
                              newItems[idx].subtotal =
                                (newItems[idx].unit_price * newItems[idx].quantity) /
                                (1 + newItems[idx].tax / 100);
                              newItems[idx].total =
                                newItems[idx].unit_price * newItems[idx].quantity;
                              setModalData((prev) => ({
                                ...prev,
                                items: newItems,
                              }));
                            }}
                            className="w/full border rounded-md px-3 py-2 text-gray-700"
                          />
                        </div>
                        <div>
                          <label className="block text-gray-700 text-sm font-medium mb-1">
                            Total (₹)
                          </label>
                          <input
                            type="number"
                            value={it.total.toFixed(2)}
                            readOnly
                            className="w/full bg-gray-100 border rounded-md px-3 py-2 text-gray-700"
                          />
                        </div>
                      </div>

                      <div className="text-gray-600 text-xs">
                        Subtotal: ₹{it.subtotal.toFixed(2)}
                      </div>
                    </div>
                  ))}

                {/* Always show “+ Add Item” at the bottom (once some items exist) */}
                {Array.isArray(modalData.items) && (
                  <button
                    onClick={() => {
                      const newItem = {
                        quantity: 1,
                        description: "",
                        sku: "",
                        unit_price: 0,
                        tax: 0,
                        subtotal: 0,
                        total: 0,
                      };
                      setModalData((prev) => ({
                        ...prev,
                        items: [...(prev.items || []), newItem],
                      }));
                    }}
                    className="inline-flex items-center px-3 py-1 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition"
                  >
                    + Add Item
                  </button>
                )}
              </div>


              {/* Grand Total & Create Order Status */}
              <div className="col-span-1 md:col-span-2">
                <h4 className="text-lg font-semibold mb-2">Totals & Status</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      Grand Total (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={modalData.grand_total}
                      onChange={(e) =>
                        setModalData((prev) => ({
                          ...prev,
                          grand_total: Number(e.target.value),
                        }))
                      }
                      className="w-full border rounded-md px-3 py-2 text-gray-700"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-1">
                      Create Order Status
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={
                        Object.values(OrderBusinessStatusEnum).find(
                          (e) =>
                            e.code ===
                            modalData.wayuvega_create_order_processing_status
                        )?.name || "No Status Exist"
                      }
                      className="w/full bg-gray-100 border rounded-md px-3 py-2 text-gray-700"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Buttons: Cancel & Save */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={closeModal}
                disabled={loadingSave}
                className={`px-6 py-2 rounded-md text-gray-800 font-medium transition ${loadingSave
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-gray-300 hover:bg-gray-400"
                  }`}
              >
                Cancel
              </button>
              <button
                onClick={saveModalChanges}
                disabled={loadingSave}
                className={`px-6 py-2 rounded-md text-white font-medium transition ${loadingSave
                  ? "bg-blue-300 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
                  }`}
              >
                {loadingSave ? (
                  <svg
                    className="animate-spin h-5 w-5 mx-auto text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

ShipmentNoteDetails.propTypes = {
  details: PropTypes.shape({
    file_id: PropTypes.string.isRequired,
    uploaded_file_name: PropTypes.string.isRequired,
    processing_status: PropTypes.string.isRequired,
    shipment_notes: PropTypes.arrayOf(
      PropTypes.shape({
        shipment_note_sequence_number: PropTypes.number.isRequired,
        order_processing_status: PropTypes.string.isRequired,
        wayuvega_create_order_processing_status: PropTypes.string,
        shipment_notes_order_data: PropTypes.shape({
          order_id: PropTypes.string.isRequired,
          order_type: PropTypes.string,
          shipment_order_type: PropTypes.string,
          order_date_utc: PropTypes.string.isRequired,
          shipping_service: PropTypes.string.isRequired,
          buyer_name: PropTypes.string.isRequired,
          seller_name: PropTypes.string.isRequired,
          drop_address_data: PropTypes.shape({
            address_line: PropTypes.string.isRequired,
            city: PropTypes.string.isRequired,
            state: PropTypes.string.isRequired,
            country_code: PropTypes.string.isRequired,
            postal_code: PropTypes.string.isRequired,
            contact: PropTypes.string.isRequired,
            email: PropTypes.string,
          }).isRequired,
          pickup_address_data: PropTypes.object,
          items: PropTypes.arrayOf(
            PropTypes.shape({
              quantity: PropTypes.number.isRequired,
              description: PropTypes.string.isRequired,
              sku: PropTypes.string.isRequired,
              unit_price: PropTypes.number.isRequired,
              subtotal: PropTypes.number.isRequired,
              tax: PropTypes.number.isRequired,
              total: PropTypes.number.isRequired,
            })
          ).isRequired,
          package_details: PropTypes.shape({
            length_in_cm: PropTypes.number.isRequired,
            breadth_in_cm: PropTypes.number.isRequired,
            height_in_cm: PropTypes.number.isRequired,
            dead_weight_in_grams: PropTypes.number.isRequired,
          }).isRequired,
          grand_total: PropTypes.number.isRequired,
        }).isRequired,
      })
    ).isRequired,
  }).isRequired,
  pickupList: PropTypes.array.isRequired,
  loadingDetails: PropTypes.bool.isRequired,
  onUseDefault: PropTypes.func.isRequired,
  onApplyPaymentAll: PropTypes.func.isRequired,
  onApplyShipmentOrderTypeAll: PropTypes.func.isRequired,
  onUpdateRow: PropTypes.func.isRequired,
  onBatchUpdateRows: PropTypes.func,
  onVerify: PropTypes.func.isRequired,
  onUnverify: PropTypes.func.isRequired,
  onBatchVerify: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};